import {
  Checkbox as CheckboxPrimitive,
  RadioGroup as RadioPrimitive,
  Switch as SwitchPrimitive,
} from 'radix-ui';
import { useId, type ComponentProps, type ReactNode } from 'react';
import { useUiLabels } from '../labels';
import { cn } from '../lib/cn';
import { FieldDescription, FieldError } from './field-message';
import { Icon } from './icon';

/** Label and help text next to a checkbox, radio or switch. The whole row is the touch target. */
function ChoiceLabel({
  htmlFor,
  label,
  description,
  descriptionId,
}: {
  htmlFor: string;
  label: ReactNode;
  description?: ReactNode;
  descriptionId: string;
}) {
  return (
    <span className="min-w-0 flex-1">
      <label htmlFor={htmlFor} className="block cursor-pointer font-medium text-fg">
        {label}
      </label>
      {description && (
        <span id={descriptionId} className="mt-0.5 block text-fg-muted">
          {description}
        </span>
      )}
    </span>
  );
}

export interface CheckboxProps extends Omit<
  ComponentProps<typeof CheckboxPrimitive.Root>,
  'children'
> {
  /** Visible label. Without it, pass `aria-label`. */
  label?: ReactNode;
  description?: ReactNode;
}

export function Checkbox({ label, description, id, className, ...props }: CheckboxProps) {
  const generated = useId();
  const controlId = id ?? generated;
  const descriptionId = `${controlId}-description`;
  const box = (
    <CheckboxPrimitive.Root
      id={controlId}
      aria-describedby={description ? descriptionId : undefined}
      className={cn(
        'peer flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-sm border border-line-strong bg-surface text-brand-solid-fg transition-colors transition-fast hover:border-fg-subtle disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-danger data-[state=checked]:border-brand-solid data-[state=checked]:bg-brand-solid data-[state=indeterminate]:border-brand-solid data-[state=indeterminate]:bg-brand-solid',
        // Bigger hit area without changing the look.
        'relative after:absolute after:-inset-3',
        !label && className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator className="group flex">
        <Icon name="check" className="size-3.5 group-data-[state=indeterminate]:hidden" />
        <Icon name="minus" className="hidden size-3.5 group-data-[state=indeterminate]:block" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
  if (!label) return box;
  return (
    <div className={cn('flex items-start gap-3 py-1', className)}>
      <span className="flex h-5 items-center">{box}</span>
      <ChoiceLabel
        htmlFor={controlId}
        label={label}
        description={description}
        descriptionId={descriptionId}
      />
    </div>
  );
}

export interface CheckboxGroupOption<T extends string = string> {
  value: T;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

export interface CheckboxGroupProps<T extends string = string> {
  /** Heading of the group, read out when focus enters it. */
  legend: ReactNode;
  options: readonly CheckboxGroupOption<T>[];
  /** Chosen values, always in the order of `options`. */
  value: readonly T[];
  onValueChange: (value: T[]) => void;
  description?: ReactNode;
  /** Error text in words, shown below the group (never colour only). */
  error?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  /** Columns from tablet width on; the phone always shows one. */
  columns?: 1 | 2 | 3;
  id?: string;
  className?: string;
}

const groupColumns = { 1: '', 2: 'md:grid-cols-2', 3: 'md:grid-cols-3' } as const;

/**
 * Several choices from a short list, e.g. the types of an organisation. Legend, help text and
 * error are wired like in `FormField`.
 */
export function CheckboxGroup<T extends string>({
  legend,
  options,
  value,
  onValueChange,
  description,
  error,
  required,
  disabled,
  columns = 1,
  id,
  className,
}: CheckboxGroupProps<T>) {
  const labels = useUiLabels();
  const generated = useId();
  const groupId = id ?? generated;
  const descriptionId = description ? `${groupId}-description` : undefined;
  const errorId = error ? `${groupId}-error` : undefined;
  return (
    <fieldset
      id={groupId}
      disabled={disabled}
      aria-describedby={[descriptionId, errorId].filter(Boolean).join(' ') || undefined}
      className={cn('flex flex-col gap-1.5', className)}
    >
      <legend className="mb-1.5 flex items-baseline gap-1.5 font-medium text-fg">
        {legend}
        {required && (
          <>
            <span aria-hidden className="text-danger">
              *
            </span>
            <span className="sr-only">, {labels.required}</span>
          </>
        )}
      </legend>
      <div className={cn('grid grid-cols-1 gap-x-4', groupColumns[columns])}>
        {options.map((option) => (
          <Checkbox
            key={option.value}
            id={`${groupId}-${option.value}`}
            label={option.label}
            description={option.description}
            disabled={option.disabled}
            checked={value.includes(option.value)}
            aria-invalid={error ? true : undefined}
            onCheckedChange={(checked) =>
              onValueChange(
                options
                  .map((o) => o.value)
                  .filter((v) => (v === option.value ? checked === true : value.includes(v))),
              )
            }
          />
        ))}
      </div>
      {description && <FieldDescription id={descriptionId}>{description}</FieldDescription>}
      {error && <FieldError id={errorId}>{error}</FieldError>}
    </fieldset>
  );
}

export interface SwitchProps extends Omit<ComponentProps<typeof SwitchPrimitive.Root>, 'children'> {
  label?: ReactNode;
  description?: ReactNode;
}

/** On/off for settings that apply immediately. Label left, switch right. */
export function Switch({ label, description, id, className, ...props }: SwitchProps) {
  const generated = useId();
  const controlId = id ?? generated;
  const descriptionId = `${controlId}-description`;
  const toggle = (
    <SwitchPrimitive.Root
      id={controlId}
      aria-describedby={description ? descriptionId : undefined}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border border-transparent bg-line-strong transition-colors transition-fast disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-brand-solid',
        'after:absolute after:-inset-3',
        !label && className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb className="block size-4 translate-x-0.5 rounded-full bg-surface shadow-raised transition-transform transition-fast data-[state=checked]:translate-x-4" />
    </SwitchPrimitive.Root>
  );
  if (!label) return toggle;
  return (
    <div className={cn('flex items-start gap-4 py-1', className)}>
      <ChoiceLabel
        htmlFor={controlId}
        label={label}
        description={description}
        descriptionId={descriptionId}
      />
      <span className="flex h-5 items-center">{toggle}</span>
    </div>
  );
}

export interface RadioOption {
  value: string;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps extends Omit<
  ComponentProps<typeof RadioPrimitive.Root>,
  'children'
> {
  options: readonly RadioOption[];
}

export function RadioGroup({ options, className, ...props }: RadioGroupProps) {
  const baseId = useId();
  return (
    <RadioPrimitive.Root className={cn('flex flex-col gap-1', className)} {...props}>
      {options.map((option) => {
        const id = `${baseId}-${option.value}`;
        return (
          <div key={option.value} className="flex items-start gap-3 py-1">
            <span className="flex h-5 items-center">
              <RadioPrimitive.Item
                id={id}
                value={option.value}
                disabled={option.disabled}
                aria-describedby={option.description ? `${id}-description` : undefined}
                className="relative flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full border border-line-strong bg-surface transition-colors transition-fast after:absolute after:-inset-3 hover:border-fg-subtle disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-brand-solid"
              >
                <RadioPrimitive.Indicator className="size-2 rounded-full bg-brand-solid" />
              </RadioPrimitive.Item>
            </span>
            <ChoiceLabel
              htmlFor={id}
              label={option.label}
              description={option.description}
              descriptionId={`${id}-description`}
            />
          </div>
        );
      })}
    </RadioPrimitive.Root>
  );
}
